import { ModelType, ShippingRegion, UserRatingInput } from '../../../graphql/API';
import { Plan } from '../../../pages/SubscriptionPlans/types';
import { ACCESS } from '../../../redux/features/subscription/access';
import { ChipOption } from '../../ChipOptions/ChipOptions';
import Tooltip, { PlusIcon, StarterIcon, TooltipContent } from './ToolTip';
import { PartialVenueListAccessRequestsFilter } from './types';

const makeOptionDisable = (
  disabled: boolean,
  plan: Plan,
  icon: ChipOption['icon'],
): Omit<ChipOption, 'label' | 'value'> | undefined => {
  return {
    disabled,
    ...(disabled ? { icon } : {}),
    ...(disabled && {
      tooltip: {
        Component: Tooltip,
        props: {
          placement: 'top',
          title: <TooltipContent plan={plan} />,
        },
      },
    }),
  };
};

export const followerOptions: (subscriptionAccess: ACCESS[] | undefined) => ChipOption[] = (
  subscriptionAccess,
) => [
  { label: '0+', value: '' },
  { label: '1k+', value: '1000' },
  { label: '5k+', value: '5000' },
  { label: '10k+', value: '10000' },
  {
    label: '25k+',
    value: '25000',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_25K_FOLLOWERS'),
      'starter',
      <StarterIcon />,
    ),
  },
  {
    label: '50k+',
    value: '50000',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_50K_FOLLOWERS'),
      'plus',
      <PlusIcon />,
    ),
  },
];

export const followerOptionsV2: (subscriptionAccess: ACCESS[] | undefined) => ChipOption[] = (
  subscriptionAccess,
) => [
  { label: '0+', value: '0' },
  { label: '1k+', value: '1000' },
  {
    label: '5k+',
    value: '5000',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_25K_FOLLOWERS'),
      'starter',
      !subscriptionAccess?.includes('FILTER_25K_FOLLOWERS') ? (
        <StarterIcon sx={{ fill: '#40E5AF' }} />
      ) : undefined,
    ),
  },
  {
    label: '10k+',
    value: '10000',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_25K_FOLLOWERS'),
      'starter',
      !subscriptionAccess?.includes('FILTER_25K_FOLLOWERS') ? (
        <StarterIcon sx={{ fill: '#40E5AF' }} />
      ) : undefined,
    ),
  },
  {
    label: '25k+',
    value: '25000',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_50K_FOLLOWERS'),
      'plus',
      !subscriptionAccess?.includes('FILTER_50K_FOLLOWERS') ? <PlusIcon /> : undefined,
    ),
  },
  {
    label: '50k+',
    value: '50000',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_50K_FOLLOWERS'),
      'plus',
      !subscriptionAccess?.includes('FILTER_50K_FOLLOWERS') ? <PlusIcon /> : undefined,
    ),
  },
];

export const followerOptionsForProducts: (isPremium?: boolean) => ChipOption[] = (
  isPremium = false,
) => [
  { label: '0+', value: '0' },
  { label: '1k+', value: '1000' },
  { label: '5k+', value: '5000', disabled: !isPremium },
  { label: '10k+', value: '10000', disabled: !isPremium },
  {
    label: '25k+',
    value: '25000',
    disabled: !isPremium,
  },
  {
    label: '50k+',
    value: '50000',
    disabled: !isPremium,
  },
];

export const ratingOptions: ChipOption[] = [
  { label: 'All', value: '' },
  { label: '4.8+', value: UserRatingInput.excellent },
  { label: '4.5+', value: UserRatingInput.good },
  { label: '4.0+', value: UserRatingInput.ok },
];

export const memberCategoryOptions = (isBrand = false): ChipOption[] => [
  { label: 'All', value: '', disabled: isBrand },
  { label: 'Lifestyle', value: 'true', disabled: isBrand },
  { label: 'Beautypass Select', value: 'false', disabled: isBrand },
];

export const verifiedOptions: (subscriptionAccess: ACCESS[] | undefined) => ChipOption[] = (
  subscriptionAccess,
) => [
  { label: 'All', value: '' },
  {
    label: 'Verified',
    value: 'true',
    ...makeOptionDisable(
      !subscriptionAccess?.includes('FILTER_25K_FOLLOWERS'),
      'starter',
      <StarterIcon />,
    ),
  },
  { label: 'Not verified', value: 'false' },
];

type FilterOutput = Record<string, (value: unknown) => PartialVenueListAccessRequestsFilter>;

type MakeDefaultValue = Record<
  string,
  (value: PartialVenueListAccessRequestsFilter | undefined) => string
>;

export const makeDefaultValue: MakeDefaultValue = {
  follower: (f) => String(f?.minFollowers ?? ''),
  instagramVerified: (f) => {
    if (f?.instagramVerified !== undefined) {
      return f.instagramVerified ? 'true' : 'false';
    }
    return '';
  },
  rating: (f) => String(f?.rating || ''),
};

export const makeFiltersOutput = (
  filters: PartialVenueListAccessRequestsFilter | undefined,
): FilterOutput => ({
  follower: (value) => ({
    ...filters,
    minFollowers: value ? Number(value) : undefined,
  }),
  rating: (value) => ({
    ...filters,
    rating: value ? (value as UserRatingInput) : undefined,
  }),
  instagramVerified: (value) => ({
    ...filters,
    instagram: value ? value === 'true' : undefined,
  }),
});

export const shippingAvailabilityOptions: ChipOption[] = [
  { label: 'Global', value: ShippingRegion.global },
  { label: 'US Only', value: ShippingRegion.us },
  { label: 'EU Only', value: ShippingRegion.eu },
];

export const SHIPPING_AVAILABILITY: Record<ShippingRegion, string> = {
  global: 'Global',
  us: 'United States',
  eu: 'Europe',
  asia: 'Asia',
  africa: 'Africa',
  middleeast: 'Middle East',
  latinamerica: 'Latin America',
  oceania: 'Oceania',
};
